import { useState, useEffect } from 'react';
import { Container, Spinner } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { getGenerationStatus } from '../../api/userApi';

const cyan = '#00e5ff';
const orange = '#ffaa44';

// Group flat slots array by planId, each plan having an array of renewal entries
const groupSlotsByPlan = (slots) => {
  const map = new Map();
  for (const s of slots) {
    const key = s.planId.toString ? s.planId.toString() : String(s.planId);
    if (!map.has(key)) map.set(key, { planId: s.planId, planName: s.planName, amount: s.amount, renewals: [] });
    map.get(key).renewals.push(s);
  }
  return [...map.values()];
};

const GenerationBonus = () => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Two-level selection: which slot group, which renewal round within it
  const [selectedPlanIdx, setSelectedPlanIdx] = useState(0);
  const [selectedRenewalIdx, setSelectedRenewalIdx] = useState(0);

  useEffect(() => {
    getGenerationStatus()
      .then((res) => setData(res.data))
      .catch(() => setError('Failed to load generation bonus data.'))
      .finally(() => setLoading(false));
  }, []);

  const planGroups = groupSlotsByPlan(data?.slots || []);
  const currentGroup = planGroups[selectedPlanIdx];
  const slot = currentGroup?.renewals?.[selectedRenewalIdx];

  // Reset renewal selection when plan changes
  const handlePlanSelect = (idx) => {
    setSelectedPlanIdx(idx);
    setSelectedRenewalIdx(0);
  };

  return (
    <div style={{ minHeight: '100vh', background: '#0a0a0a', paddingBottom: 48 }}>
      <Container className="pt-4" style={{ maxWidth: 680 }}>

        <button
          onClick={() => navigate('/dashboard')}
          style={{ background: 'none', border: 'none', color: cyan, fontSize: 13, cursor: 'pointer', marginBottom: 20, padding: 0 }}
        >
          ‹ Back to Dashboard
        </button>

        <h5 style={{ color: '#fff', fontWeight: 700, marginBottom: 24 }}>Generation Bonus</h5>

        {loading ? (
          <div className="text-center py-5">
            <Spinner animation="border" style={{ color: cyan }} />
          </div>
        ) : error ? (
          <div style={{ color: '#ff6666', textAlign: 'center', marginTop: 40 }}>{error}</div>
        ) : (
          <>
            {/* Total Earned */}
            <div style={{
              background: 'radial-gradient(ellipse at 0% 100%, rgba(102,255,153,0.1) 0%, #0d0d0d 60%)',
              border: '1px solid rgba(102,255,153,0.2)',
              borderRadius: 14,
              padding: '24px 28px',
              marginBottom: 28,
            }}>
              <div style={{ color: '#888', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 8 }}>
                Total Generation Bonus Earned
              </div>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 10 }}>
                <span style={{ fontSize: 42, fontWeight: 900, color: '#66ff99', textShadow: '0 0 24px rgba(102,255,153,0.3)' }}>
                  {data.totalEarned.toFixed(2)}
                </span>
                <span style={{ color: '#aaa', fontSize: 16, fontWeight: 500 }}>USDT</span>
              </div>
            </div>

            {/* Slot Tabs — level 1 */}
            <div style={{ color: '#555', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 8 }}>
              Select Slot
            </div>
            <div style={{
              display: 'flex',
              gap: 8,
              overflowX: 'auto',
              paddingBottom: 4,
              marginBottom: 12,
              WebkitOverflowScrolling: 'touch',
              scrollbarWidth: 'none',
            }}>
              {planGroups.map((g, idx) => {
                const anyActive = g.renewals.some((r) => r.isSelfActive);
                return (
                  <button
                    key={String(g.planId)}
                    onClick={() => handlePlanSelect(idx)}
                    style={{
                      flexShrink: 0,
                      padding: '6px 16px',
                      borderRadius: 20,
                      border: selectedPlanIdx === idx ? `1px solid ${cyan}` : '1px solid rgba(255,255,255,0.1)',
                      background: selectedPlanIdx === idx ? 'rgba(0,229,255,0.12)' : 'transparent',
                      color: selectedPlanIdx === idx ? cyan : '#555',
                      fontWeight: 600,
                      fontSize: 12,
                      cursor: 'pointer',
                      position: 'relative',
                      transition: 'all 0.2s',
                    }}
                  >
                    {g.planName}
                    {anyActive && (
                      <span style={{
                        width: 6, height: 6, borderRadius: '50%',
                        background: '#66ff99',
                        position: 'absolute', top: 4, right: 4,
                        boxShadow: '0 0 4px rgba(102,255,153,0.6)',
                      }} />
                    )}
                  </button>
                );
              })}
            </div>

            {/* Renewal round tabs — level 2 (only if more than one round exists) */}
            {currentGroup && currentGroup.renewals.length > 1 && (
              <>
                <div style={{ color: '#555', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 8 }}>
                  Select Round
                </div>
                <div style={{ display: 'flex', gap: 6, marginBottom: 20, flexWrap: 'wrap' }}>
                  {currentGroup.renewals.map((r, ri) => (
                    <button
                      key={r.renewalLabel}
                      onClick={() => setSelectedRenewalIdx(ri)}
                      style={{
                        padding: '4px 14px',
                        borderRadius: 16,
                        border: selectedRenewalIdx === ri
                          ? `1px solid ${r.renewalCount === 0 ? cyan : orange}`
                          : '1px solid rgba(255,255,255,0.1)',
                        background: selectedRenewalIdx === ri
                          ? r.renewalCount === 0 ? 'rgba(0,229,255,0.1)' : 'rgba(255,170,68,0.1)'
                          : 'transparent',
                        color: selectedRenewalIdx === ri
                          ? r.renewalCount === 0 ? cyan : orange
                          : '#555',
                        fontWeight: 600,
                        fontSize: 11,
                        cursor: 'pointer',
                      }}
                    >
                      {r.renewalLabel}
                    </button>
                  ))}
                </div>
              </>
            )}

            {slot && (
              <>
                {/* Per-slot stats chips */}
                <div style={{ display: 'flex', gap: 10, marginBottom: 20, flexWrap: 'wrap' }}>
                  <div style={{
                    background: 'rgba(0,229,255,0.07)',
                    border: '1px solid rgba(0,229,255,0.15)',
                    borderRadius: 8, padding: '10px 18px',
                  }}>
                    <div style={{ color: '#555', fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                      Active Directs
                    </div>
                    <div style={{ color: cyan, fontSize: 22, fontWeight: 800, marginTop: 2 }}>
                      {slot.directCount}
                    </div>
                    <div style={{ color: '#444', fontSize: 10, marginTop: 2 }}>
                      {slot.planName} · {slot.renewalLabel}
                    </div>
                  </div>
                  <div style={{
                    background: slot.isSelfActive ? 'rgba(102,255,153,0.07)' : 'rgba(255,170,68,0.07)',
                    border: `1px solid ${slot.isSelfActive ? 'rgba(102,255,153,0.2)' : 'rgba(255,170,68,0.2)'}`,
                    borderRadius: 8, padding: '10px 18px',
                  }}>
                    <div style={{ color: '#555', fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                      Self Status
                    </div>
                    <div style={{ color: slot.isSelfActive ? '#66ff99' : orange, fontSize: 15, fontWeight: 700, marginTop: 4 }}>
                      {slot.isSelfActive ? '✓ Active' : '○ Inactive'}
                    </div>
                    <div style={{ color: '#444', fontSize: 10, marginTop: 2 }}>{slot.renewalLabel}</div>
                  </div>
                </div>

                {/* Level Eligibility Table */}
                <div style={{ color: '#aaa', fontSize: 13, fontWeight: 600, marginBottom: 12 }}>
                  Level Eligibility — {slot.planName} ({slot.renewalLabel})
                </div>
                <div style={{ borderRadius: 12, overflow: 'hidden', border: '1px solid rgba(0,229,255,0.12)' }}>

                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: '70px 80px 1fr 50px',
                    background: 'rgba(0,229,255,0.07)',
                    borderBottom: '1px solid rgba(0,229,255,0.15)',
                    padding: '12px 20px',
                  }}>
                    {['Level', 'Bonus', 'Eligibility', 'Status'].map((h) => (
                      <div key={h} style={{ color: cyan, fontWeight: 600, fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                        {h}
                      </div>
                    ))}
                  </div>

                  {slot.levels.map((row, idx) => (
                    <div
                      key={row.level}
                      style={{
                        display: 'grid',
                        gridTemplateColumns: '70px 80px 1fr 50px',
                        padding: '14px 20px',
                        alignItems: 'center',
                        borderBottom: idx < slot.levels.length - 1 ? '1px solid rgba(255,255,255,0.04)' : 'none',
                        background: row.active ? 'rgba(102,255,153,0.04)' : 'transparent',
                        borderLeft: row.active ? '3px solid rgba(102,255,153,0.4)' : '3px solid transparent',
                      }}
                    >
                      <div style={{
                        width: 42, height: 42, borderRadius: '50%', flexShrink: 0,
                        background: row.active ? 'rgba(102,255,153,0.15)' : 'rgba(255,255,255,0.05)',
                        border: `1px solid ${row.active ? 'rgba(102,255,153,0.35)' : 'rgba(255,255,255,0.08)'}`,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: 11, fontWeight: 800,
                        color: row.active ? '#66ff99' : '#555',
                      }}>
                        {row.ordinal}
                      </div>

                      <div style={{ color: row.active ? '#66aaff' : '#444', fontWeight: 700, fontSize: 14 }}>
                        {row.bonus}
                      </div>

                      <div>
                        <div style={{ color: row.active ? '#aaa' : '#444', fontSize: 12 }}>{row.eligibility}</div>
                        {row.level > 1 && (
                          <div style={{ color: '#555', fontSize: 10, marginTop: 2 }}>
                            {slot.directCount}/{row.required} active directs
                          </div>
                        )}
                      </div>

                      <div style={{ textAlign: 'center' }}>
                        <span style={{ fontSize: 20, color: row.active ? '#66ff99' : '#444', display: 'block', lineHeight: 1 }}>
                          {row.active ? '✓' : '○'}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}

            <div style={{ color: '#444', fontSize: 11, textAlign: 'center', marginTop: 20 }}>
              Bonuses are credited to your Earning Wallet when eligible upline levels are unlocked.
              Each renewal round (R1, R2…) is tracked independently.
            </div>
          </>
        )}
      </Container>
    </div>
  );
};

export default GenerationBonus;
